import {Pipe, PipeTransform} from '@angular/core';
import {Participant} from "../../domain/models";
import Fuse from 'fuse.js';

@Pipe({
  name: 'filterList',
  pure: false
})
export class FilterListPipe implements PipeTransform {
  private fuse = new Fuse([], {
    keys: [
      'name',
      'surname',
      'club',
      'phone',
      'bib',
      'dateOfBirth',
      {
        name: 'fullName',
        getFn: (obj: any) => {
          return `${obj.surname || ''} ${obj.name || ''}`.trim();
        }
      },
      {
        name: 'reverseFullName',
        getFn: (obj: any) => {
          return `${obj.name || ''} ${obj.surname || ''}`.trim();
        }
      },
      {
        name: 'allBibNumbers',
        getFn: (obj: any) => {
          if (!obj.allBibNumbers || !Array.isArray(obj.allBibNumbers)) {
            return '';
          }
          return obj.allBibNumbers.map((bibItem: any) =>
            `${bibItem.bib || ''} ${bibItem.event || ''}`
          ).join(' ');
        }
      }
    ],
    threshold: 0.3,
    minMatchCharLength: 2,
    shouldSort: true,
    includeScore: true,
    findAllMatches: true
  });

  private cache = new Map<string, any[]>();
  private lastItems: any[] = [];
  private lastSearchText: string = '';

  transform(items: Participant[], searchText: string): any[] {
    if (!items) {
      return [];
    }

    if (!searchText || searchText.trim() === '') {
      return items;
    }

    const cacheKey = `${searchText}_${items.length}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    if (items.length > 1000 && searchText.length < 3) {
      const result = this.simpleFilter(items, searchText);
      this.cache.set(cacheKey, result);
      return result;
    }

    this.fuse.setCollection(items);
    const searchResults = this.fuse.search(searchText);

    const result = searchResults
      .map(result => result.item)
      .sort((a, b) => {
        if (a.isFromOtherEvent && !b.isFromOtherEvent) {
          return 1;
        }
        if (!a.isFromOtherEvent && b.isFromOtherEvent) {
          return -1;
        }
        return 0;
      });

    const limitedResult = result.slice(0, 500);

    this.cache.set(cacheKey, limitedResult);

    if (this.cache.size > 10) {
      const keys = Array.from(this.cache.keys());
      keys.slice(0, keys.length - 10).forEach(key => this.cache.delete(key));
    }

    return limitedResult;
  }

  private simpleFilter(items: Participant[], searchText: string): Participant[] {
    const query = searchText.toLowerCase().trim();

    const searchParts = query.split(/\s+/).filter(part => part.length > 0);

    return items.filter(item => {
      const surname = (item.surname || '').toLowerCase();
      const name = (item.name || '').toLowerCase();

      if (searchParts.length >= 2) {
        const firstPart = searchParts[0];
        const secondPart = searchParts[1];

        const surnameFirstMatch = surname.startsWith(firstPart) && name.startsWith(secondPart);
        const nameFirstMatch = name.startsWith(firstPart) && surname.startsWith(secondPart);

        const surnameContainsFirst = surname.includes(firstPart);
        const nameContainsFirst = name.includes(firstPart);
        const surnameContainsSecond = surname.includes(secondPart);
        const nameContainsSecond = name.includes(secondPart);

        if ((surnameContainsFirst && nameContainsSecond) ||
            (nameContainsFirst && surnameContainsSecond) ||
            surnameFirstMatch ||
            nameFirstMatch) {
          return true;
        }

        const fullNameWithoutSpaces = (surname + name).replace(/\s+/g, '');
        const searchWithoutSpaces = query.replace(/\s+/g, '');
        if (fullNameWithoutSpaces.includes(searchWithoutSpaces)) {
          return true;
        }
      }

      return (
        (item.name && item.name.toLowerCase().includes(query)) ||
        (item.surname && item.surname.toLowerCase().includes(query)) ||
        (item.bib && item.bib.toString().includes(query)) ||
        (item.phone && item.phone.includes(query)) ||
        (item.club && item.club.toLowerCase().includes(query))
      );
    }).sort((a, b) => {
      if (a.isFromOtherEvent && !b.isFromOtherEvent) {
        return 1;
      }
      if (!a.isFromOtherEvent && b.isFromOtherEvent) {
        return -1;
      }
      return 0;
    }).slice(0, 500);
  }
}
